/**
 * Company: SPHMedia
 * Description: [Inline video player component for homepage video package]
 */

import MuteButton from "@assets/quick-news/Mute.png";
import PlayIcon from "@assets/quick-news/play-icon.png";
import UnMuteButton from "@assets/quick-news/Unmute.png";
import { BrightcoveVideo } from "@components/Blocks/BrightcoveVideo/BrightcoveVideo";
import { KAKI_MAKAN_AUTOPLAY_PLAYER_ID } from "@config/kakiMakan";
import stickyController from "@helper/stickyController";
import type {
  BrightcovePlayer,
  LatestVideoResponse,
} from "@typings/Brightcove.d";
import {
  Dispatch,
  ReactElement,
  RefObject,
  SetStateAction,
  useRef,
} from "react";

import useInlineVideoPlayer from "./hooks/useInlineVideoPlayer";
import useStickyInlinePlayer from "./hooks/useStickyInlinePlayer";

type InlineVideoPlayerProps = {
  playing: LatestVideoResponse;
  forwardedRef?: RefObject<HTMLDivElement | null>;
  observerRef?: RefObject<HTMLDivElement | null>;
  pipContainerRef?: RefObject<HTMLDivElement | null>;
  isSticky?: boolean;
  setIsSticky?: Dispatch<SetStateAction<boolean>>;
  isMute: boolean;
  setIsMute: Dispatch<SetStateAction<boolean>>;
  onEnded?: () => void;
  nextPlaylist?: () => void;
  pageRefValue?: string;
  syncMuteAcrossPlayers?: boolean;
};

/**
 * InlineVideoPlayer
 * Description: Renders an inline video player with video details such as name and publication date.
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function InlineVideoPlayer(
  props: InlineVideoPlayerProps,
): ReactElement {
  const {
    playing,
    forwardedRef,
    onEnded,
    nextPlaylist,
    pageRefValue = "home_ringkas",
    isSticky,
    setIsSticky,
    observerRef,
    pipContainerRef,
    isMute,
    setIsMute,
    syncMuteAcrossPlayers = true,
  } = props;
  const { id } = playing;
  const inlineContainerRef = useRef<HTMLDivElement | null>(null);
  const playerWrapperRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<BrightcovePlayer | null>(null);
  const playerId = "inline-video-player";
  const {
    isPlaying,
    toggleMute,
    togglePlay,
    handlePlayerReady,
    handlePlayerExpand,
  } = useInlineVideoPlayer({
    playerRef,
    pageRefValue,
    setIsMute,
  });
  useStickyInlinePlayer({
    isMute,
    isPlaying,
    isSticky,
    setIsSticky,
    playerId,
    observerRef,
    pipContainerRef,
    inlineContainerRef,
    playerWrapperRef,
  });

  return (
    <>
      <div ref={inlineContainerRef} className="min-h-[366px]">
        <div
          ref={playerWrapperRef}
          className="flex flex-col mx-auto bg-bg-primary transition-all duration-300"
        >
          <div className="relative">
            <div className="w-full">
              <BrightcoveVideo
                videoId={id}
                forwardedRef={forwardedRef}
                onEnded={onEnded}
                nextPlaylist={nextPlaylist}
                playerStyle={"!aspect-[206/366]"}
                playerId={KAKI_MAKAN_AUTOPLAY_PLAYER_ID}
                onPlayerReady={handlePlayerReady}
                syncMuteAcrossPlayers={syncMuteAcrossPlayers}
              />
            </div>

            {isSticky && (
              <div className="z-20 absolute top-0 right-0 flex items-center justify-center w-8 h-8 p-1 rounded-full bg-black-500">
                <button
                  type="button"
                  aria-label="Close picture-in-picture"
                  onClick={() => {
                    setIsSticky?.(false);
                    stickyController.clear(playerId);
                    playerRef.current?.pause();
                  }}
                  className="text-text-inverse text-base outline-none focus:outline-none"
                >
                  X
                </button>
              </div>
            )}

            <div
              className="absolute inset-0 w-full h-full z-10"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 80%, rgba(0,0,0,0.8) 100%)",
              }}
            >
              <button
                type="button"
                onClick={togglePlay}
                className="w-full h-full"
                aria-label="Toggle Play"
              />
            </div>

            {!isPlaying && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center transition-opacity duration-300 opacity-100">
                <img src={PlayIcon} alt="Play Video" width={40} height={40} />
              </div>
            )}

            <div className="absolute bottom-0 z-30 w-full bg-gradient-to-t from-neutral-1000 to-transparent p-1">
              <div className="flex items-center justify-between p-2">
                {/* Mute/Unmute */}
                <button type="button" onClick={toggleMute} className="flex">
                  <img
                    src={isMute ? MuteButton : UnMuteButton}
                    alt={isMute ? "Mute" : "Unmute"}
                    width={18}
                    height={17}
                  />
                </button>

                {/* Expand */}
                <button
                  type="button"
                  onClick={handlePlayerExpand}
                  className="flex"
                >
                  <span className="before:content-['\f108'] font-['VideoJS'] text-md text-neutral-0 leading-[100%]"></span>
                  <span className="sr-only">Expand</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
